import { type ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import clsx from 'clsx';

import { SxProps } from '../../../../types';
import { tokens } from '../../../../theme/generated/tokens';
import { classNamesFor } from '../../../../utils/classNames';
import { mergeSx } from '../../../../utils';

// ---------------------------------------------------------------------------
// Class names
// ---------------------------------------------------------------------------

const treeMapTooltipClassNamesFactory = classNamesFor('TreeMapTooltip');

export const treeMapTooltipClassNames = {
  root: treeMapTooltipClassNamesFactory.root(),
  header: treeMapTooltipClassNamesFactory.element('header'),
  title: treeMapTooltipClassNamesFactory.element('title'),
  status: treeMapTooltipClassNamesFactory.element('status'),
  body: treeMapTooltipClassNamesFactory.element('body'),
  trendRow: treeMapTooltipClassNamesFactory.element('trendRow'),
  trendChip: treeMapTooltipClassNamesFactory.element('trendChip'),
  dataRows: treeMapTooltipClassNamesFactory.element('dataRows'),
  dataRow: treeMapTooltipClassNamesFactory.element('dataRow'),
  dataRowLabel: treeMapTooltipClassNamesFactory.element('dataRowLabel'),
  dataRowValue: treeMapTooltipClassNamesFactory.element('dataRowValue'),
  footer: treeMapTooltipClassNamesFactory.element('footer'),
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TreeMapTooltipRow = {
  /** Label shown on the left. */
  label: string;
  /** Value shown on the right. */
  value: string;
  /**
   * Optional color override for the value.
   * When set, the value renders in P/10-B (SemiBold) instead of P/10-M.
   * Use `tokens.theme.fg.status.success` for positive savings values.
   */
  valueColor?: string;
};

export type TreeMapTooltipProps = {
  /**
   * Primary title in the header (e.g. cluster name).
   * Rendered as A/2 — Bai Jamjuree Bold 12px.
   */
  title: string;
  /**
   * Optional status text on the right of the header (e.g. "Read only").
   * Rendered as P/10-B in `fg.status.success` by default.
   */
  status?: string;
  /** Override color for the status text. */
  statusColor?: string;
  /**
   * Label for the trend highlight row (e.g. "Potential cost impact").
   * Requires `trendValue` to render.
   */
  trendLabel?: string;
  /**
   * Value shown in the green TrendChip (e.g. "20.24%").
   * Shown uppercase with a ↘ arrow icon. Requires `trendLabel`.
   */
  trendValue?: string;
  /** Data rows shown below the trend row. */
  rows?: TreeMapTooltipRow[];
  /**
   * Optional footer below the body (e.g. agent install CTA).
   * Rendered under a top border when present.
   */
  footer?: ReactNode;
  /** `data-testid` on the root element. */
  testId?: string;
  /** MUI sx prop. */
  sx?: SxProps;
  /** Additional CSS class. */
  className?: string;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * TreeMapTooltip — rich tooltip card matching the Figma design for TreeMap cells.
 *
 * Structure:
 *  - Header: bold title (A/2) + optional status label (P/10-B)
 *  - Trend row: label + green TrendChip with ↘ icon and value
 *  - Data rows: label/value pairs (P/10-R / P/10-M)
 *
 * @example
 * <TreeMapTooltip
 *   title="Prod-africa"
 *   status="Read only"
 *   trendLabel="Potential cost impact"
 *   trendValue="20.24%"
 *   rows={[
 *     { label: 'Current cost',   value: '$756,362' },
 *     { label: 'Optimized cost', value: '$594,362' },
 *     { label: 'Savings',        value: '$124,362', valueColor: tokens.theme.fg.status.success },
 *   ]}
 * />
 */
export const TreeMapTooltip = ({
  title,
  status,
  statusColor,
  trendLabel,
  trendValue,
  rows = [],
  footer,
  testId,
  sx,
  className,
}: TreeMapTooltipProps) => {
  const showTrendRow = Boolean(trendLabel && trendValue);
  const showBody = showTrendRow || rows.length > 0;

  return (
    <Stack
      className={clsx(treeMapTooltipClassNames.root, className)}
      data-testid={testId ?? 'tree-map-tooltip'}
      sx={mergeSx(
        {
          backgroundColor: tokens.theme.bg.structural.surface,
          color: tokens.theme.fg.structural.primary,
          overflow: 'hidden',
          borderRadius: tokens.theme.radius.sm,
          border: '1px solid',
          borderColor: tokens.theme.border.structural.neutral,
          // Keep Figma content width; prevent edge clamping from shrinking rows.
          width: 'max-content',
          minWidth: 251,
        },
        sx
      )}
    >
      <Stack
        direction="row"
        alignItems="center"
        className={treeMapTooltipClassNames.header}
        sx={{
          px: '12px',
          py: '8px',
          gap: '4px',
          borderBottom: showBody ? '1px solid' : undefined,
          borderColor: tokens.theme.border.structural.neutral,
          overflow: 'hidden',
        }}
      >
        <Typography
          variant="A2"
          className={treeMapTooltipClassNames.title}
          sx={{
            flex: '1 0 0',
            minWidth: 0,
            color: tokens.theme.fg.structural.primary,
            lineHeight: '18px',
          }}
        >
          {title}
        </Typography>

        {status && (
          <Typography
            variant="P10B"
            className={treeMapTooltipClassNames.status}
            sx={{
              flexShrink: 0,
              color: statusColor ?? tokens.theme.fg.status.success,
              whiteSpace: 'nowrap',
            }}
          >
            {status}
          </Typography>
        )}
      </Stack>
      {showBody && (
        <Stack
          className={treeMapTooltipClassNames.body}
          sx={{ px: '12px', py: '8px', gap: '4px' }}
        >
          {showTrendRow && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              className={treeMapTooltipClassNames.trendRow}
              sx={{ py: '2px' }}
            >
              <Typography
                variant="P10R"
                sx={{
                  color: tokens.theme.fg.structural.primary,
                  whiteSpace: 'nowrap',
                }}
              >
                {trendLabel}
              </Typography>

              <Stack
                direction="row"
                alignItems="center"
                className={treeMapTooltipClassNames.trendChip}
                sx={{
                  gap: '4px',
                  height: '20px',
                  px: '8px',
                  borderRadius: tokens.theme.radius.full,
                  backgroundColor: tokens.theme.bg.status.successStrong,
                  flexShrink: 0,
                }}
              >
                {/* Figma: ArrowDownRight icon 12×12 */}
                <Box
                  component="svg"
                  viewBox="0 0 12 12"
                  sx={{
                    width: '12px',
                    height: '12px',
                    flexShrink: 0,
                    display: 'block',
                  }}
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2.5 2.5L9.5 9.5M9.5 9.5V3.5M9.5 9.5H3.5"
                    stroke={tokens.theme.fg.status.onSuccessStrong}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Box>

                <Typography
                  variant="L10M"
                  sx={{
                    color: tokens.theme.fg.status.onSuccessStrong,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap',
                    lineHeight: '12px',
                  }}
                >
                  {trendValue}
                </Typography>
              </Stack>
            </Stack>
          )}

          {rows.length > 0 && (
            <Stack
              className={treeMapTooltipClassNames.dataRows}
              sx={{ gap: 0 }}
            >
              {rows.map((row) => (
                <Stack
                  key={row.label}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  className={treeMapTooltipClassNames.dataRow}
                  sx={{ height: '20px', py: '2px', width: '227px' }}
                >
                  <Typography
                    variant="P10R"
                    className={treeMapTooltipClassNames.dataRowLabel}
                    sx={{
                      color: tokens.theme.fg.structural.primary,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.label}
                  </Typography>

                  <Typography
                    variant={row.valueColor ? 'P10B' : 'P10M'}
                    className={treeMapTooltipClassNames.dataRowValue}
                    sx={{
                      color:
                        row.valueColor ?? tokens.theme.fg.structural.secondary,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      )}
      {footer ? (
        <Box
          className={treeMapTooltipClassNames.footer}
          sx={{
            px: '12px',
            py: '8px',
            borderTop: '1px solid',
            borderColor: tokens.theme.border.structural.neutral,
            width: '100%',
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Stack>
  );
};
